import React, { useContext, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { AccountResponseDTO, IsActiveEnum } from '_lib/api/apiTypes.shim';

import { InfoAlert } from '_lib/components/Alert/Alert';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { Chip } from '_lib/components/Chip/Chip';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';
import { Typography } from '_lib/components/Typography/Typography';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';

import { AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';

export type ReactivateAccountDialogProps = AsyncDialogProps & {
  account: AccountResponseDTO;
};

const ReactivateAccountDialog = (props: ReactivateAccountDialogProps) => {
  const { account, onClose, ...dialogProps } = props;

  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>([]);

  const api = useContext(ApiContext);

  const accountCompaniesQuery = api.companies.useUnpagedList({
    params: { accountId: account.id, serializer: 'name', isActive: IsActiveEnum.Any }
  });

  const accountReactivateMutation = api.accounts.useReactivateWithCompanies();

  const handleCancel = () => {
    onClose({ action: 'CANCEL' });
  };

  const handleConfirm = async () => {
    try {
      const reactivateAllCompanies =
        !!accountCompaniesQuery?.data && selectedCompanyIds.length === accountCompaniesQuery.data.length;

      await accountReactivateMutation.patch({
        id: account.id,
        data: {
          reactivateCompanyIds: reactivateAllCompanies ? [] : selectedCompanyIds,
          reactivateAllCompanies
        }
      });
      onClose({ action: 'CONFIRM' });
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData.nonFieldErrors);
      }
    }
  };

  const isFetchingOrPending = accountCompaniesQuery.isFetching || accountReactivateMutation.isPending;

  return (
    <DialogWrapper onClose={onClose} titleText='Reactivate Account' {...dialogProps}>
      <DialogContent sx={{ minWidth: '450px' }}>
        <BackdropSpinner open={isFetchingOrPending} />
        <Stack spacing={2.5} padding={2}>
          {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
          {!isFetchingOrPending && !!accountCompaniesQuery?.data && accountCompaniesQuery.data.length === 0 && (
            <InfoAlert>
              <Typography>No Companies for this Account</Typography>
            </InfoAlert>
          )}
          {!!accountCompaniesQuery?.data && accountCompaniesQuery.data.length > 0 && (
            <>
              <Stack direction='row' sx={{ justifyContent: 'center' }}>
                <Button
                  sx={{ textTransform: 'none' }}
                  text='Select All'
                  color='primary'
                  variant='contained'
                  disabled={selectedCompanyIds.length === accountCompaniesQuery.data.length}
                  onClick={() => setSelectedCompanyIds(accountCompaniesQuery.data.map((company) => company.id))}
                />
                <Button
                  sx={{ textTransform: 'none' }}
                  text='Clear All'
                  color='cancel'
                  variant='contained'
                  disabled={selectedCompanyIds.length === 0}
                  onClick={() => setSelectedCompanyIds([])}
                />
              </Stack>
              <TextField
                label='Select Companies'
                value={selectedCompanyIds}
                select
                slotProps={{
                  select: {
                    multiple: true,
                    renderValue: (values) => {
                      if (values && Array.isArray(values) && values.length) {
                        return (
                          <FlexBox sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {values.map((companyId) => (
                              <Chip
                                key={companyId}
                                label={
                                  accountCompaniesQuery.data.find((company) => company.id === companyId)?.name || ''
                                }
                              />
                            ))}
                          </FlexBox>
                        );
                      }
                      return 'Select Companies';
                    }
                  }
                }}
                required
                onChange={(event) => {
                  const {
                    target: { value }
                  } = event;
                  setSelectedCompanyIds(typeof value === 'string' ? value.split(',') : value);
                }}>
                {accountCompaniesQuery.data.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    <Checkbox checked={selectedCompanyIds.includes(company.id)} />
                    <ListItemText primary={company.name} />
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <DialogButton color='cancel' onClick={handleCancel} text='Cancel' />
        <DialogButton onClick={handleConfirm} text='Reactivate' disabled={isFetchingOrPending} />
      </DialogActions>
    </DialogWrapper>
  );
};

export const useReactivateAccountDialog = () => useAsyncDialog(ReactivateAccountDialog);
